import Axios from 'axios';
import { BigNumber } from 'bignumber.js';
import { action, observable } from 'mobx';
import { OrderParams, PageInfo, ScrollPageInfo } from '@/models/data';
import { Notification } from 'element-react';
import { API } from '@/config/api';
import i18n from '@/i18n';
import { getBaseUrlBySymbol, getBaseUrlByUnderlyingType } from '@/utils/futures';
import UnderLyingEnum from '@/@enums/UnderLyingEnum';
import { TpslConditionType } from '@/pages/futures/main/orderForm/tpslCondition';
import { TriggerPriceType } from '@/pages/futures/main/orderForm/formSection';
import { parseOrderResponse } from '@/utils/parseOrderResponse';
import {
  getBizErrorCode,
  triggerWelfareOppositeOpenModal,
  WELFARE_OPPOSITE_OPEN_CODE,
} from '@/components/welfareOppositeOpenModal/bridge';

export interface OrderItem {
  positionModel: string;
  positionType: string;
  avgPrice: string; //	成交均价
  closePosition: boolean; //	是否条件全平仓
  createdTime: number; //	创建时间
  updatedTime?: number; // 修改接口要求的订单快照时间
  sourceId: string; //	条件触发id
  executedQty: string; //	已成交数量
  marginFrozen: string; //	占用保证金
  orderId: string; //	订单id
  orderSide: OrderSide; //	买卖方向
  orderType: string; //	订单类型
  origQty: string; // 	数量
  positionSide: PositionSide; // 	持仓方向
  price: string; //	委托价格
  state: OrderState; //	订单状态 1：新建订单（未成交）；2：部分成交；3：全部成交；4：已撤销；5：平台撤销（拒绝）；6：已过期
  symbol: string; //	交易对
  timeInForce: string; // 有效类型
  closeProfit: string; // 平仓盈亏
  forceClose?: boolean; // 是否爆仓
  triggerProfitPrice?: string;
  triggerProfitConditionType?: TpslConditionType;
  triggerProfitConditionValue?: string;
  triggerStopPrice?: string;
  triggerStopConditionType?: TpslConditionType;
  triggerStopConditionValue?: string;
  triggerPriceType?: TriggerPriceType;
  // 其他配置
  contractSize: string;
  fee?: string | number;
  tradeFee?: string | number;
  feeCoin?: string;
}

export interface OrderBillItem {
  fee: number;
  feeCoin: string;
  orderId: number;
  price: number;
  quantity: number;
  symbol: string;
  timestamp: number;
}

export default class Order {
  @observable orderList: OrderItem[];

  @observable orderListPageInfo: PageInfo;

  @observable orderRecord: OrderItem[];

  @observable orderRecordPageInfo: ScrollPageInfo;

  @observable withdrawOrderList: string[];

  @observable orderBillList: OrderBillItem[];

  @observable orderBillListPageInfo: PageInfo;

  @observable isFetchingOrderList: boolean;

  @observable isFetchingOrderRecord: boolean;

  /** 成交明细 Tab：getTradeList page/size/total */
  @observable orderRecordTradePageInfo: PageInfo;

  @observable isFetchingOrderBillList: boolean;

  @observable isFetchingLiquidationRecord: boolean;

  @observable liquidationRecord: OrderItem[];

  @observable liquidationRecordPageInfo: ScrollPageInfo;

  constructor() {
    this.orderList = [];
    this.orderListPageInfo = {
      page: 1,
      size: 10,
      total: 0,
    };

    this.orderRecord = [];
    this.orderRecordPageInfo = {
      hasPrev: false,
      hasNext: false,
    };

    this.orderRecordTradePageInfo = {
      page: 1,
      size: 10,
      total: 0,
    };

    this.withdrawOrderList = [];

    this.orderBillList = [];
    this.orderBillListPageInfo = {
      page: 1,
      size: 10,
      total: 0,
    };

    this.liquidationRecord = [];
    this.liquidationRecordPageInfo = {
      hasPrev: false,
      hasNext: false,
    };

    this.isFetchingOrderList = false;
    this.isFetchingOrderRecord = false;
    this.isFetchingOrderBillList = false;
    this.isFetchingLiquidationRecord = false;
  }

  @action.bound
  setLoadingState(key: string, data: boolean) {
    // @ts-ignore
    this[key] = data;
  }

  @action
  setOrderList(data: OrderItem[]) {
    this.orderList = data;
  }

  @action.bound
  setOrderListPageInfo(data: PageInfo) {
    const origin = { ...this.orderListPageInfo };
    this.orderListPageInfo = { ...origin, ...data };
  }

  @action
  setOrderRecord(data: OrderItem[]) {
    this.orderRecord = data;
  }

  @action.bound
  setOrderRecordPageInfo(data: ScrollPageInfo) {
    const origin = { ...this.orderRecordPageInfo };
    this.orderRecordPageInfo = { ...origin, ...data };
  }

  @action.bound
  async fetchOrderList(params?: {
    symbol?: string;
    page?: number;
    size?: number;
    state?: string;
    underlyingType?: UnderLyingEnum;
  }) {
    this.setLoadingState('isFetchingOrderList', true);
    const orderListPageInfo = this.orderListPageInfo;

    const config: any = {
      params: {
        page: orderListPageInfo.page,
        size: orderListPageInfo.size,
        ...params,
      },
    };

    if (params?.symbol) {
      config.baseURL = getBaseUrlBySymbol(params.symbol);
    } else if (params?.underlyingType) {
      config.baseURL = getBaseUrlByUnderlyingType(params?.underlyingType);
    }

    Reflect.deleteProperty(config.params, 'underlyingType');

    const result: any = await Axios.get(API.ORDER_LIST, config);

    if (result) {
      const { items, page, ps: size, total } = result;

      if (params?.state !== 'HISTORY') {
        if (Array.isArray(items)) {
          this.setOrderList(items);

          this.setOrderListPageInfo({ page, size, total });
        }
      }
    }

    this.setLoadingState('isFetchingOrderList', false);
  }

  @action.bound
  async fetchOrderHistory(
    {
      symbol,
      direction,
      limit = 10,
      id,
      startTime,
      endTime,
      underlyingType,
      source,
      page,
      /** 成交明细 Tab 走 getTradeList；历史委托限价勿开 */
      aggregateTrades,
      /** @deprecated 历史委托限价/市价已改回 getHistoryList，勿再开启 */
      positionHistory,
    }: {
      symbol?: string;
      direction?: ScrollPaginationDirection;
      limit?: number;
      id?: string;
      startTime?: number | string;
      endTime?: number | string;
      underlyingType?: UnderLyingEnum;
      /** Step28 聚合 source；NORMAL 不传以保持原 page/size 分页 */
      source?: string;
      page?: number;
      aggregateTrades?: boolean;
      positionHistory?: boolean;
    },
    retried = false,
  ) {
    this.setLoadingState('isFetchingOrderRecord', true);

    // 成交明细 Tab 使用 page/size；其余历史订单使用游标分页。
    const useTradeList = Boolean(aggregateTrades);
    const usePositionHistory = Boolean(positionHistory);
    const usePagePagination = useTradeList;
    const tradePage = page ?? 1;
    const tradeSize = limit;
    const config: any = {
      transformResponse: [parseOrderResponse],
      params: usePagePagination
        ? {
          symbol,
          page: tradePage,
          size: tradeSize,
          startTime,
          endTime,
          ...(source ? { source } : {}),
        }
        : usePositionHistory
          ? {
            symbol,
            direction,
            limit,
            id,
            startTime,
            endTime,
            ...(source ? { source } : {}),
          }
          : {
            symbol,
            direction,
            limit,
            id: id ?? '',
            startTime,
            endTime,
            contractType: 'PERPETUAL',
          },
    };

    if (!usePositionHistory && symbol) {
      config.baseURL = getBaseUrlBySymbol(symbol);
    } else if (!usePositionHistory && underlyingType) {
      config.baseURL = getBaseUrlByUnderlyingType(underlyingType);
    }

    try {
      const url = usePositionHistory
        ? API.POSITION_HISTORY_V2
        : useTradeList
          ? API.ORDER_BILL_LIST
          : API.ORDER_HISTORY;
      const result: any = await Axios.get(url, config);

      if (result) {
        const { hasPrev, hasNext, items, page: resPage, ps, size: resSize, total } = result;
        const mapped = (items || []).map((item: any) => {
          const positionHistoryQty =
            item.closeOrderSize ?? item.maxPositionSize ?? item.positionSize ?? item.quantity;
          const positionHistoryState =
            item.state ??
            item.orderState ??
            item.status ??
            (item.finished === true ? 'FILLED' : item.finished === false ? 'PARTIALLY_FILLED' : undefined);

          return {
            ...item,
            closeOrderId: item.closeOrderId != null ? String(item.closeOrderId) : undefined,
            execId: item.execId != null ? String(item.execId) : '',
            // V2 历史仓位主键是 id，兼容后端返回的平仓订单 ID。
            orderId: String(item.orderId ?? item.closeOrderId ?? item.id ?? item.positionId ?? item.execId ?? ''),
            origQty: String(item.origQty ?? positionHistoryQty ?? item.executedQty ?? '0'),
            executedQty: String(item.executedQty ?? positionHistoryQty ?? item.origQty ?? '0'),
            orderType: item.orderType ?? item.closeOrderType ?? item.type,
            state: positionHistoryState,
            price: String(item.price ?? item.orderPrice ?? item.entryPrice ?? item.avgPrice ?? '0'),
            avgPrice: String(item.avgPrice ?? item.closePrice ?? item.price ?? item.entryPrice ?? '0'),
            tradeFee: item.tradeFee ?? item.fee ?? '0',
            createdTime: item.createdTime ?? item.closeTime ?? item.updatedTime ?? item.timestamp,
            source: item.source || (item.isExperiencePosition ? 'POSITION_EXPERIENCE' : 'NORMAL'),
          };
        });
        this.setOrderRecord(mapped);
        if (usePagePagination) {
          const resolvedPage = resPage ?? tradePage;
          const resolvedSize = ps ?? resSize ?? tradeSize;
          const resolvedTotal = Number(total ?? 0);
          this.orderRecordTradePageInfo = {
            page: resolvedPage,
            size: resolvedSize,
            total: resolvedTotal,
          };
          this.setOrderRecordPageInfo({
            hasPrev: resolvedPage > 1,
            hasNext:
              resolvedTotal > 0
                ? resolvedPage * resolvedSize < resolvedTotal
                : Boolean(hasNext),
          });
        } else {
          this.setOrderRecordPageInfo({
            hasPrev: Boolean(hasPrev),
            hasNext: Boolean(hasNext),
          });
        }
      }
    } catch {
      if (aggregateTrades && source && !retried) {
        await this.fetchOrderHistory(
          { symbol, direction, limit, id, startTime, endTime, underlyingType, aggregateTrades: false },
          true,
        );
      }
    } finally {
      this.setLoadingState('isFetchingOrderRecord', false);
    }
  }

  // @action.bound
  // updateOrderListByRow = debounce((params: OrderItem) => {
  //   const { orderId } = params;
  //   const index = this.orderList.findIndex(item => item.orderId === orderId);
  //
  //   const newList = [...this.orderList];
  //   if (index > -1) {
  //     const target = newList[index];
  //     const newItem = {
  //       ...target,
  //       ...params,
  //     };
  //
  //     if (ORDER_UNCOMPLETED_STATE_LIST.includes(params.state)) {
  //       newList.splice(index, 1, newItem);
  //     } else {
  //       newList.splice(index, 1);
  //     }
  //   } else if (ORDER_UNCOMPLETED_STATE_LIST.includes(params.state)) {
  //     newList.unshift(params);
  //   }
  //
  //   this.setOrderList(newList);
  // }, 300);

  @action.bound
  async fetchWithdrawOrder(id: string) {
    this.setWithdrawOrderList([...this.withdrawOrderList, id]);
    const result: any = await Axios.post(API.ORDER_CANCEL, { orderId: id }).catch(({ error }: any) => {
      Notification.error({
        message: error?.msg || i18n.t('failure.withdraw'),
        offset: 60,
        duration: 2000,
      });
    });

    if (result) {
      const newOrderList = [...this.orderList];
      const index = newOrderList.findIndex((item: OrderItem) => id === item.orderId);
      newOrderList.splice(index, 1);
      this.setOrderList(newOrderList);

      const { page, size, total } = this.orderListPageInfo;
      this.setOrderListPageInfo({ page, size, total: total - 1 });

      Notification.success({
        message: i18n.t('success.withdraw'),
        offset: 60,
        duration: 2000,
      });

      this.setWithdrawOrderList([...this.withdrawOrderList.filter(item => item !== id)]);
    }
  }

  /** K 线改单：调用 commission/updateOrder 原子修改未成交限价委托。 */
  @action.bound
  async fetchAmendOrderPrice(item: OrderItem, nextPrice: number | string, nextQty?: number | string) {
    const priceNum = Number(nextPrice);
    if (!item?.orderId || !Number.isFinite(priceNum) || priceNum <= 0) {
      return { ok: false as const };
    }

    const prevRemain = new BigNumber(item.origQty || 0).minus(item.executedQty || 0);
    const remaining = nextQty == null || nextQty === '' ? prevRemain : new BigNumber(nextQty);
    if (!remaining.isFinite() || remaining.lte(0)) {
      return { ok: false as const };
    }
    try {
      if (!item.updatedTime) {
        await this.fetchOrderList({ symbol: item.symbol, page: 1, state: 'UNFINISHED' });
        Notification.error({ message: i18n.t('failure.amendOrder', { defaultValue: '订单信息已更新，请刷新后重试。' }), offset: 60, duration: 2000 });
        return { ok: false as const };
      }
      const params: Record<string, string | number> = {
        orderId: item.orderId,
        updatedTime: item.updatedTime,
        price: priceNum,
      };
      if (nextQty != null && nextQty !== '') {
        // 弹框输入的是修改后的剩余数量，接口要求传总委托数量。
        params.origQty = new BigNumber(item.executedQty || 0).plus(remaining).toFixed();
      }
      if (item.triggerProfitPrice) {
        params.triggerProfitPrice = item.triggerProfitPrice;
        if (item.triggerProfitConditionType) params.triggerProfitConditionType = item.triggerProfitConditionType;
        if (item.triggerProfitConditionValue != null) params.triggerProfitConditionValue = item.triggerProfitConditionValue;
      }
      if (item.triggerStopPrice) {
        params.triggerStopPrice = item.triggerStopPrice;
        if (item.triggerStopConditionType) params.triggerStopConditionType = item.triggerStopConditionType;
        if (item.triggerStopConditionValue != null) params.triggerStopConditionValue = item.triggerStopConditionValue;
      }
      if (item.triggerPriceType) params.triggerPriceType = item.triggerPriceType;
      const result: any = await Axios.post(API.ORDER_UPDATE, params);
      if (getBizErrorCode(result) === WELFARE_OPPOSITE_OPEN_CODE) {
        triggerWelfareOppositeOpenModal();
        await this.fetchOrderList({ symbol: item.symbol, page: 1, state: 'UNFINISHED' });
        return { ok: false as const, code: WELFARE_OPPOSITE_OPEN_CODE };
      }
      Notification.success({
        message: i18n.t('success.amendOrder', { defaultValue: '修改委托成功。' }),
        offset: 60,
        duration: 2000,
      });
      await this.fetchOrderList({ symbol: item.symbol, page: 1, state: 'UNFINISHED' });
      return { ok: true as const, result };
    } catch (err: any) {
      if (getBizErrorCode(err) === WELFARE_OPPOSITE_OPEN_CODE) {
        triggerWelfareOppositeOpenModal();
      }
      Notification.error({
        message: err?.error?.msg || err?.error || err?.msgInfo || err?.msg || i18n.t('failure.amendOrder', { defaultValue: '修改委托失败。' }),
        offset: 60,
        duration: 2000,
      });
      await this.fetchOrderList({ symbol: item.symbol, page: 1, state: 'UNFINISHED' });
      return { ok: false as const };
    }
  }

  @action.bound
  async fetchUpdateOrderProfit(params: {
    orderId: string;
    triggerProfitPrice?: string;
    triggerProfitConditionType?: TpslConditionType;
    triggerProfitConditionValue?: string;
    triggerStopPrice?: string;
    triggerStopConditionType?: TpslConditionType;
    triggerStopConditionValue?: string;
    triggerPriceType?: TriggerPriceType;
  }) {
    const result: any = await Axios.post(API.ORDER_PROFIT_UPDATE, params).catch(({ error }: any) => {
      Notification.error({
        message: error?.msg || i18n.t('failure.updateStopProfit'),
        offset: 60,
        duration: 2000,
      });
    });

    if (result) {
      const index = this.orderList.findIndex(item => item.orderId === params.orderId);
      if (index > -1) {
        const nextList = [...this.orderList];
        nextList[index] = { ...nextList[index], ...params };
        this.setOrderList(nextList);
      }
      Notification.success({
        message: i18n.t('success.updateStopProfit'),
        offset: 60,
        duration: 2000,
      });
      return result;
    }

    return null;
  }

  // 撤销所有订单 当前委托
  @action.bound
  async fetchWithdrawOrderAll(symbol?: string, positionSource: 'ALL' | 'SELF' | 'EXPERIENCE' = 'ALL') {
    const result: any = await Axios.post(API.ORDER_CANCEL_ALL, { symbol, positionSource }).catch(({ error }: any) => {
      Notification.error({
        message: error?.msg || i18n.t('failure.withdraw'),
        offset: 60,
        duration: 2000,
      });
    });

    if (result) {
      const data = {
        page: 1,
        state: 'UNFINISHED',
        symbol,
      };
      this.fetchOrderList(data);

      Notification.success({
        message: i18n.t('success.withdraw'),
        offset: 60,
        duration: 2000,
      });
      return Promise.resolve(result);
    }
  }

  // 更新现有订单列表
  // 如果是本地更新，当前页不是第一页时没有必要更新；
  // @action.bound
  // addOrderToList(newItem: OrderItem, isLocal = false) {
  //   const { page, size, total } = this.orderListPageInfo;
  //   if (isLocal) {
  //     // 如果不是第一页，没有必要将本地产生的订单加入列表；
  //     if (page !== 1) return;
  //   }
  //
  //   const newList = [newItem, ...this.orderList];
  //   if (newList.length > size) {
  //     newList.pop();
  //   }
  //
  //   this.setOrderList(newList);
  //   this.setOrderListPageInfo({ page, size, total: total + 1 });
  // }

  @action.bound
  setWithdrawOrderList(data: string[]) {
    this.withdrawOrderList = data;
  }

  @action.bound
  isWithdrawingOrder(id: string) {
    return this.withdrawOrderList.includes(id);
  }

  @action.bound
  setOrderBillList(data: OrderBillItem[]) {
    this.orderBillList = data;
  }

  @action.bound
  setOrderBillListPageInfo(data: PageInfo) {
    const origin = { ...this.orderBillListPageInfo };
    this.orderBillListPageInfo = { ...origin, ...data };
  }

  @action.bound
  async fetchOrderBillList(params: { orderId: string; page?: number; size?: number }) {
    this.setLoadingState('isFetchingOrderBillList', true);

    const result: any = await Axios.get(API.ORDER_BILL_LIST, {
      params: { ...params, orderId: String(params.orderId) },
    });

    if (result) {
      const { items, page, ps: size, total } = result;

      if (Array.isArray(items)) {
        this.setOrderBillList(items);

        this.setOrderBillListPageInfo({ page, size, total });
      }
    }

    this.setLoadingState('isFetchingOrderBillList', false);
  }

  @action.bound
  async fetchCreateOrder(params: OrderParams) {
    try {
      const result: any = await Axios.post(API.ORDER_CREATE, params);
      if (getBizErrorCode(result) === WELFARE_OPPOSITE_OPEN_CODE) {
        triggerWelfareOppositeOpenModal();
        return { ok: false as const, code: WELFARE_OPPOSITE_OPEN_CODE };
      }
      if (result) {
        Notification.success({
          message: i18n.t('success.order'),
          offset: 60,
          duration: 2000,
        });
        // 所有下单入口（表单、K 线快捷下单、平仓）共享此处刷新。
        // K 线通过 observable orderList 更新委托线，无需等待延迟轮询/推送。
        // 查询失败不能把已经成功的下单报告为失败，后续推送仍会同步。
        void this.fetchOrderList({
          symbol: localStorage.getItem('isShowCurrentSymbolOnly') === 'true' ? params.symbol : undefined,
          page: 1,
          state: 'UNFINISHED',
        }).catch(() => undefined);
      }
      return { ok: true as const, result };
    } catch (err: any) {
      if (getBizErrorCode(err) === WELFARE_OPPOSITE_OPEN_CODE) {
        triggerWelfareOppositeOpenModal();
        return { ok: false as const, code: WELFARE_OPPOSITE_OPEN_CODE };
      }
      Notification.error({
        message: err?.error?.msg || err?.error || err?.msgInfo || err?.msg || i18n.t('failure.order'),
        offset: 60,
        duration: 2000,
      });
      return { ok: false as const };
    }
  }

  //
  // 强平记录
  //

  @action
  setLiquidationRecord(data: OrderItem[]) {
    this.liquidationRecord = data;
  }

  @action.bound
  setLiquidationRecordPageInfo(data: ScrollPageInfo) {
    const origin = { ...this.liquidationRecordPageInfo };
    this.liquidationRecordPageInfo = { ...origin, ...data };
  }

  @action.bound
  async fetchLiquidationRecord({
    symbol,
    direction,
    limit = 20,
    id,
    startTime,
    endTime,
  }: {
    symbol?: string;
    direction: ScrollPaginationDirection;
    limit?: number;
    id?: string;
    startTime?: number;
    endTime?: number;
  }) {
    this.setLoadingState('isFetchingLiquidationRecord', true);

    const result: any = await Axios.get(API.ORDER_HISTORY, {
      params: {
        symbol,
        direction,
        limit,
        id,
        startTime,
        endTime,
        forceClose: true,
      },
    });

    if (result) {
      const { hasPrev, hasNext, items } = result;

      this.setLiquidationRecord(items || []);
      this.setLiquidationRecordPageInfo({ hasPrev, hasNext });
    }

    this.setLoadingState('isFetchingLiquidationRecord', false);
  }
}
